export async function processImagesAndResize(
  oldHtml: string,
  newHtml: string,
  uploadFileFn: (file: File) => Promise<string>,
  deleteFileFn: (url: string) => Promise<void>,
): Promise<string> {
  let finalHtml = newHtml;
  const oldImages = extractImageInfo(oldHtml);
  const imgTagRegex = /<img\b[^>]*>/gi;
  const matches = finalHtml.match(imgTagRegex) || [];
  const deletedUrls = new Set<string>();

  for (const originalImgTag of matches) {
    const srcValue = getImgAttribute(originalImgTag, 'src');
    if (!srcValue) continue;
    const { width: wExtracted, height: hExtracted } = getImgDimensions(originalImgTag);
    let w = wExtracted;
    let h = hExtracted;

    if (srcValue.startsWith('data:image/')) {
      const file = base64ToFile(srcValue);
      if (w === 0 || h === 0) {
        const { finalW, finalH } = await fallbackIfZero(file, w, h);
        w = finalW;
        h = finalH;
      }
      const resizedBlob = await resizeImage(file, w, h);
      if (!resizedBlob) continue;
      const newFile = new File([resizedBlob], file.name, { type: file.type });
      const uploadedUrl = await uploadFileFn(newFile);
      if (uploadedUrl) {
        const replacedTag = originalImgTag.replace(srcValue, uploadedUrl);
        finalHtml = finalHtml.replace(originalImgTag, replacedTag);
      }
      continue;
    }

    const oldInfo = oldImages[srcValue];
    if (!oldInfo) continue;
    if (w !== oldInfo.width || h !== oldInfo.height) {
      const newUrl = await downloadAndReuploadExisting(srcValue, w, h, uploadFileFn);
      if (newUrl) {
        const replacedTag = originalImgTag.replace(srcValue, newUrl);
        finalHtml = finalHtml.replace(originalImgTag, replacedTag);
        if (!deletedUrls.has(srcValue)) {
          await deleteFileFn(srcValue);
          deletedUrls.add(srcValue);
        }
      }
    }
  }

  for (const oldUrl of Object.keys(oldImages)) {
    if (oldUrl.startsWith('data:image/')) continue;
    if (!finalHtml.includes(oldUrl) && !deletedUrls.has(oldUrl)) {
      await deleteFileFn(oldUrl);
      deletedUrls.add(oldUrl);
    }
  }
  return finalHtml;
}

function extractImageInfo(html: string): Record<string, { width: number; height: number }> {
  const map: Record<string, { width: number; height: number }> = {};
  const imgTagRegex = /<img\b[^>]*>/gi;
  const matches = html.match(imgTagRegex) || [];
  for (const tag of matches) {
    const src = getImgAttribute(tag, 'src');
    if (!src) continue;
    const { width, height } = getImgDimensions(tag);
    map[src] = { width, height };
  }
  return map;
}

function getImgAttribute(tag: string, attribute: string): string | null {
  const parser = new DOMParser();
  const doc = parser.parseFromString(tag, 'text/html');
  const img = doc.querySelector('img');
  if (img) {
    return img.getAttribute(attribute);
  }
  return null;
}

function getImgDimensions(tag: string): { width: number; height: number } {
  const parser = new DOMParser();
  const doc = parser.parseFromString(tag, 'text/html');
  const img = doc.querySelector('img');
  let width = 0;
  let height = 0;
  if (img) {
    if (img.style.width) {
      width = parseInt(img.style.width, 10);
    }
    if (img.style.height) {
      height = parseInt(img.style.height, 10);
    }
    if (!width) {
      const attrW = img.getAttribute('width');
      if (attrW) {
        width = parseInt(attrW, 10);
      }
    }
    if (!height) {
      const attrH = img.getAttribute('height');
      if (attrH) {
        height = parseInt(attrH, 10);
      }
    }
  }
  return { width, height };
}

async function downloadAndReuploadExisting(
  oldUrl: string,
  w: number,
  h: number,
  uploadFileFn: (file: File) => Promise<string>,
): Promise<string> {
  try {
    const resp = await fetch(oldUrl);
    if (!resp.ok) return '';
    const blob = await resp.blob();
    const file = new File([blob], 'oldImage.png', { type: blob.type });
    const { finalW, finalH } = await fallbackIfZero(file, w, h);
    const resizedBlob = await resizeImage(file, finalW, finalH);
    if (!resizedBlob) return '';
    const newFile = new File([resizedBlob], file.name, { type: file.type });
    return await uploadFileFn(newFile);
  } catch {
    return '';
  }
}

function base64ToFile(base64: string): File {
  const arr = base64.split(',');
  const mimeMatch = arr[0].match(/:(.*?);/);
  const mime = mimeMatch ? mimeMatch[1] : 'image/png';
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new File([u8arr], 'image.png', { type: mime });
}

async function fallbackIfZero(
  file: File,
  w: number,
  h: number,
): Promise<{ finalW: number; finalH: number }> {
  if (w !== 0 && h !== 0) return { finalW: w, finalH: h };
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      resolve({ finalW: img.width, finalH: img.height });
    };
    img.onerror = () => {
      resolve({ finalW: 0, finalH: 0 });
    };
    const reader = new FileReader();
    reader.onload = (e) => {
      img.src = e.target?.result as string;
    };
    reader.readAsDataURL(file);
  });
}

async function resizeImage(file: File, w: number, h: number): Promise<Blob | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        resolve(null);
        return;
      }
      ctx.drawImage(img, 0, 0, w, h);
      canvas.toBlob((blob) => resolve(blob), file.type, 0.85);
    };
    img.onerror = () => resolve(null);
    const reader = new FileReader();
    reader.onload = (e) => {
      img.src = e.target?.result as string;
    };
    reader.readAsDataURL(file);
  });
}

export function convertStyleAttrToSafeStyleAttr(content: string): string {
  const parser = new DOMParser();
  const doc = parser.parseFromString(content, 'text/html');

  doc.querySelectorAll<HTMLElement>('[style]').forEach((el) => {
    const style = el.getAttribute('style');
    if (style !== null) {
      el.removeAttribute('style');
      el.setAttribute('data-style-original', style);
    }
  });

  return doc.body.innerHTML;
}

export function convertIframeTagToSafeIframeAttr(content: string): string {
  const parser = new DOMParser();
  const doc = parser.parseFromString(content, 'text/html');

  doc.querySelectorAll<HTMLIFrameElement>('iframe').forEach((iframe) => {
    const originalHtml = iframe.outerHTML;

    const placeholder = document.createElement('div');
    placeholder.setAttribute('data-iframe-original', originalHtml);

    iframe.replaceWith(placeholder);
  });

  return doc.body.innerHTML;
}
